/**
 * The pieces every widget is built from (Home spec 2026-09-23 §4 "A card"):
 * an uppercase title with an optional switch and a "→" link, a headline
 * figure with its qualifier, whole-row links, one quiet line, the failed
 * read's line, and a skeleton. Module widgets may import these too.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui'
import { readUnavailable, type ReadState } from '@/components/records/readState'

export function WidgetHead({ title, link, children }: { title: string; link?: { to: string; text: string }; children?: ReactNode }) {
  return (
    <div className="flex min-h-6 flex-wrap items-center gap-2.5">
      <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-muted-foreground">{title}</span>
      {children}
      {link && <Link to={link.to} className="ml-auto whitespace-nowrap text-[12.5px] text-primary hover:underline">{link.text} →</Link>}
    </div>
  )
}

/** F17 (spec D4, issue AC): every headline number links to the list it
 * counted. `to`, when given, wraps the VALUE (never the note) in a router
 * `Link`. */
export function Headline({ value, note, to }: { value: ReactNode; note: string; to?: string }) {
  return (
    <div className="text-[30px] font-semibold leading-[1.1] tracking-tight tabular-nums">
      {to ? <Link to={to} className="hover:underline">{value}</Link> : value}
      <span className="ml-2 text-[12.5px] font-normal tracking-normal text-muted-foreground">{note}</span>
    </div>
  )
}

/** A whole-row link: bold first line, muted second, an optional chip, and
 * (SA-1170, design pass spec §8.2) an optional `code` — a record number, a
 * STRING (SA-1179 D1: a `ReactNode` let `code={false}` render an empty span)
 * — rendered LAST, small and muted, so the first line reads company, route,
 * size; an empty string or null is no span. A caller that shows a number
 * passes it here, never in `top`. SA-1174 (design note D9): an optional
 * `lead` renders FIRST (My work's stripe and step badge), and a row with no
 * `sub` is one line — no empty span (`sub={null}` keeps its empty span). */
export function WidgetRow({ to, state, top, sub, right, code, lead }: {
  to: string; state?: unknown; top: ReactNode; sub?: ReactNode; right?: ReactNode; code?: string | null; lead?: ReactNode
}) {
  return (
    <Link to={to} state={state} className="group flex items-center gap-3 border-t border-border py-[7px] text-[13px] first:border-t-0">
      {lead}
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="font-medium group-hover:text-primary">{top}</span>
        {sub !== undefined && <span className="text-xs text-muted-foreground">{sub}</span>}
      </span>
      {right}
      {typeof code === 'string' && code !== '' && <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{code}</span>}
    </Link>
  )
}

/** A card's headline view (SA-1406): the figure it leads with while its read stands, else the skeleton or the
 * unavailable line, as the full card shows them. */
export function HeadlineFigure({ read, value, note, to }: {
  read: ReadState<unknown> & { isPending: boolean; error?: Error | null }; value: ReactNode; note: string; to: string
}) {
  if (read.isPending) return <WidgetSkeleton />
  if (isUnavailable(read)) return <Unavailable read={read} />
  return <Headline value={value} note={note} to={to} />
}

export function Quiet({ children }: { children: ReactNode }) {
  return <p className="m-0 text-[12.5px] text-muted-foreground">{children}</p>
}

/** The failed-with-nothing-cached line (spec D4): "unavailable" and the error. */
export function Unavailable({ read }: { read: ReadState<unknown> & { error?: Error | null } }) {
  return <p role="status" className="m-0 text-[12.5px] text-muted-foreground">unavailable{read.error?.message ? ` · ${read.error.message}` : ''}</p>
}

export const isUnavailable = (read: ReadState<unknown>) => readUnavailable(read)

export function WidgetSkeleton() {
  return <div className="space-y-2" aria-hidden="true"><Skeleton className="h-8 w-24" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
}
